import { Entity, UniqueEntityID } from '@/domain/core'
import { MomentIndex, StationType, VariableArm, WBEnvelope, WBStations } from '@/domain/models/WBProfileAirplane'
import { FuelQuantityUnit, LinearMeasureUnit, WeightUnit } from '@/domain/protocols/Unit'

export interface WBTemplateStations {
  id: UniqueEntityID // UUID
  name: string
  namePT?: string
  type: StationType
  fixedArm?: number
  maxCapacity?: number
  capacityUnit?: FuelQuantityUnit
  includeInBOW?: boolean
  predefinedWeight?: number
  armType?: 'fixed' | 'variable'
  variableArms?: VariableArm[]
  priority?: number
}

export interface WBProfileAirplaneTemplateProps {
  aircraftModelId: string
  name: string
  namePT?: string
  reference: string
  weightUnit: WeightUnit
  armUnit: LinearMeasureUnit
  fuelQuantityUnit: FuelQuantityUnit
  momentIndex: MomentIndex
  lemac?: number
  macLength?: number
  basicEmptyWeight?: number
  basicEmptyWeightArm?: number
  maximumTakeoffWeight: number
  maximumZeroFuelWeight?: number
  maximumRampWeight?: number
  maximumLandingWeight?: number

  stations: WBTemplateStations[]
  envelope: WBEnvelope[]
}

export interface WBProfileAirplaneTemplatePojo extends WBProfileAirplaneTemplateProps {
  id: string
}

export class WBProfileAirplaneTemplate extends Entity<WBProfileAirplaneTemplateProps, WBProfileAirplaneTemplatePojo> {
  private constructor(props: WBProfileAirplaneTemplateProps, id?: UniqueEntityID) {
    super(props, id)
  }

  public static create(props: WBProfileAirplaneTemplateProps, id?: UniqueEntityID): WBProfileAirplaneTemplate {
    return new WBProfileAirplaneTemplate(props, id)
  }

  // Getters

  public get aircraftModelId(): string {
    return this.props.aircraftModelId
  }

  public get name(): string {
    return this.props.name
  }

  public get namePT(): string | undefined {
    return this.props.namePT
  }

  public get reference(): string {
    return this.props.reference
  }

  public get weightUnit(): WeightUnit {
    return this.props.weightUnit
  }

  public get armUnit(): LinearMeasureUnit {
    return this.props.armUnit
  }

  public get fuelQuantityUnit(): FuelQuantityUnit {
    return this.props.fuelQuantityUnit
  }

  public get momentIndex(): MomentIndex {
    return this.props.momentIndex
  }

  public get lemac(): number | undefined {
    return this.props.lemac
  }

  public get macLength(): number | undefined {
    return this.props.macLength
  }

  public get basicEmptyWeight(): number | undefined {
    return this.props.basicEmptyWeight
  }

  public get basicEmptyWeightArm(): number | undefined {
    return this.props.basicEmptyWeightArm
  }

  public get maximumTakeoffWeight(): number {
    return this.props.maximumTakeoffWeight
  }

  public get maximumZeroFuelWeight(): number | undefined {
    return this.props.maximumZeroFuelWeight
  }

  public get maximumRampWeight(): number | undefined {
    return this.props.maximumRampWeight
  }

  public get maximumLandingWeight(): number | undefined {
    return this.props.maximumLandingWeight
  }

  public get stations(): WBStations[] {
    return this.props.stations
  }

  public get envelope(): WBEnvelope[] {
    return this.props.envelope
  }

  // Setters

  public set aircraftModelId(value: string) {
    this.props.aircraftModelId = value
  }

  public set name(value: string) {
    this.props.name = value
  }

  public set namePT(value: string | undefined) {
    this.props.namePT = value
  }

  public set reference(value: string) {
    this.props.reference = value
  }

  public set weightUnit(value: WeightUnit) {
    this.props.weightUnit = value
  }

  public set armUnit(value: LinearMeasureUnit) {
    this.props.armUnit = value
  }

  public set fuelQuantityUnit(value: FuelQuantityUnit) {
    this.props.fuelQuantityUnit = value
  }

  public set momentIndex(value: MomentIndex) {
    this.props.momentIndex = value
  }

  public set lemac(value: number | undefined) {
    this.props.lemac = value
  }

  public set macLength(value: number | undefined) {
    this.props.macLength = value
  }

  public set basicEmptyWeight(value: number | undefined) {
    this.props.basicEmptyWeight = value
  }

  public set basicEmptyWeightArm(value: number | undefined) {
    this.props.basicEmptyWeightArm = value
  }

  public set maximumTakeoffWeight(value: number) {
    this.props.maximumTakeoffWeight = value
  }

  public set maximumZeroFuelWeight(value: number | undefined) {
    this.props.maximumZeroFuelWeight = value
  }

  public set maximumRampWeight(value: number | undefined) {
    this.props.maximumRampWeight = value
  }

  public set maximumLandingWeight(value: number | undefined) {
    this.props.maximumLandingWeight = value
  }

  public set stations(value: WBStations[]) {
    this.props.stations = value
  }

  public set envelope(value: WBEnvelope[]) {
    this.props.envelope = value
  }
}
